import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronRight, FiChevronDown, FiArrowRight } from 'react-icons/fi';
import PDLProgressBadge from './PDLProgressBadge';

const ACTION_BTN = {
  CONNECT_ENEDIS: { label: 'Connecter Enedis', actionParam: 'connect' },
  WAIT_FILES:     { label: 'Voir détail',      actionParam: null },
  OPTIMIZE:       { label: 'Optimiser',        actionParam: 'optimize' },
};

function formatMWh(kwh) {
  if (kwh == null) return '—';
  return `${(kwh / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} MWh`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR');
}

/**
 * Groupe d'optimisation par compte/entité.
 * - 1 ligne "compte" avec chevron expand/collapse + résumé agrégé.
 * - N lignes "PDL" enfants visibles uniquement quand expanded.
 */
export default function OptimizablePDLAccountGroup({ account, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const navigate = useNavigate();

  const total = account.pdls.length;
  const ready = account.pdls.filter(p => p.progress_pct === 100).length;
  const minPct = Math.min(...account.pdls.map(p => p.progress_pct));

  // État global du compte = palier le plus bas (= le plus de travail à faire)
  const minPdl = account.pdls.find(p => p.progress_pct === minPct) || account.pdls[0];
  const conf = minPdl.enedis_flags_unknown
    ? ACTION_BTN.WAIT_FILES
    : (ACTION_BTN[minPdl.next_action] || ACTION_BTN.WAIT_FILES);
  // Aucun PDL prêt et au moins un PDL aux drapeaux ENEDIS illisibles : le compte
  // n'est pas « À connecter », sa progression est inconnue.
  const accountUnknown = ready === 0 && account.pdls.some(p => p.enedis_flags_unknown);

  const handleAccountClick = (e) => {
    e.stopPropagation();
    setExpanded(v => !v);
  };

  const handlePDLClick = (pdl) => {
    const params = new URLSearchParams({ tab: 'acheminement', pdl: pdl.pdl_number });
    if (conf.actionParam) params.set('action', conf.actionParam);
    // La route /dashboard/admin/client/:id attend un opportunity_id, pas un account_id.
    // Fallback sur account_id si opportunity_id absent (ne devrait pas arriver vu le filtre mandat).
    const targetId = pdl.opportunity_id || pdl.account_id;
    navigate(`/dashboard/admin/client/${targetId}?${params.toString()}`);
  };

  return (
    <>
      {/* Ligne compte (toujours visible) */}
      <tr
        onClick={handleAccountClick}
        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer text-[11px]"
      >
        <td className="px-2 py-1.5 w-6 text-gray-400">
          {expanded
            ? <FiChevronDown className="w-3 h-3" />
            : <FiChevronRight className="w-3 h-3" />}
        </td>
        <td className="px-2 py-1.5 font-semibold text-gray-900">{account.company_name || '—'}</td>
        <td className="px-2 py-1.5 text-gray-600">
          {total} PDL{total > 1 ? 's' : ''}
        </td>
        <td className="px-2 py-1.5 text-gray-700">{account.commercial_name || 'Non assigné'}</td>
        <td className="px-2 py-1.5 text-gray-700">—</td>
        <td className="px-2 py-1.5 text-gray-700">—</td>
        <td className="px-2 py-1.5 text-gray-700">—</td>
        <td className="px-2 py-1.5">
          {ready > 0 && ready < total
            ? <PDLProgressBadge partial label={`Partiel · ${ready}/${total} prêts`} />
            : <PDLProgressBadge pct={minPct} label={ready === total ? `${ready}/${total} prêts` : null} unknown={accountUnknown} />}
        </td>
        <td className="px-2 py-1.5 text-right">
          <span className="text-[10px] text-gray-400">
            {ready}/{total}
          </span>
        </td>
      </tr>

      {/* Lignes PDL enfants */}
      {expanded && account.pdls.map(pdl => {
        // Drapeaux ENEDIS inconnus : ne pas pousser « Connecter Enedis » à tort.
        const pdlConf = pdl.enedis_flags_unknown
          ? ACTION_BTN.WAIT_FILES
          : (ACTION_BTN[pdl.next_action] || ACTION_BTN.WAIT_FILES);
        return (
          <tr key={pdl.pdl_id} className="border-b border-gray-100 bg-gray-50/40 text-[11px]">
            <td className="px-2 py-1.5 w-6"></td>
            <td className="px-2 py-1.5 pl-6 text-gray-700 italic">↳ {pdl.site_name || '—'}</td>
            <td className="px-2 py-1.5 text-gray-700">{pdl.pdl_number}</td>
            <td className="px-2 py-1.5 text-gray-500">—</td>
            <td className="px-2 py-1.5 text-gray-700">{formatDate(pdl.connected_at)}</td>
            <td className="px-2 py-1.5 text-gray-700">{formatMWh(pdl.annual_consumption_kwh)}</td>
            <td className="px-2 py-1.5 text-gray-700">{pdl.subscribed_power_kva ?? '—'}</td>
            <td className="px-2 py-1.5">
              <PDLProgressBadge pct={pdl.progress_pct} label={pdl.progress_label} unknown={!!pdl.enedis_flags_unknown} />
            </td>
            <td className="px-2 py-1.5 text-right">
              <button
                onClick={(e) => { e.stopPropagation(); handlePDLClick(pdl); }}
                className="inline-flex items-center gap-1 rounded bg-teal-600 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-teal-700"
              >
                {pdlConf.label} <FiArrowRight className="w-2.5 h-2.5" />
              </button>
            </td>
          </tr>
        );
      })}
    </>
  );
}
